import {Highlight as HighlightType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/highlight.types';
import {ShopStreamItemV2_HighlightsSection_Highlight} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {Fade} from '@aboutyou/transitions';
import {
  Button,
  COLORS,
  ErrorBoundary,
  GenericIconName,
  HorizontalSlider,
  Icon,
  Z_INDEXES,
  desktopAndUp,
  useExpensiveValueByViewport,
  useKeyPress,
  useLockBodyScroll,
} from '@aboutyou/ui-components';
import React, {useCallback, useEffect} from 'react';
import styled, {css} from 'styled-components';
import {HighlightSlide} from './HighlightSlide';
import {useGetCenteredSlideIndex} from './useGetCenteredSlideIndex';

export const BrandHighlightsZoomSlider: React.FC<{
  highlights: HighlightType[] | ShopStreamItemV2_HighlightsSection_Highlight[];
  isOpen: boolean;
  hide: () => void;
  openHighlight: (index: number) => void;
  currentSlide?: number;
  disableOverscroll?: boolean;
}> = ({
  isOpen,
  hide,
  highlights,
  currentSlide,
  openHighlight,
  disableOverscroll,
}) => {
  useLockBodyScroll({lockScroll: isOpen && !disableOverscroll});
  useKeyPress({key: 'Escape', onKeyDown: hide, disabled: !isOpen});

  const slideCount = useExpensiveValueByViewport(
    {small: 1, medium: 3, large: 3},
    1,
  );

  const disableNavigationControls = useExpensiveValueByViewport(
    {small: true, medium: false, large: false},
    true,
  );

  const addFakeOuterSlideElements = useExpensiveValueByViewport(
    {small: false, medium: true, large: true},
    false,
  );

  const {centeredHighlightIndex, handleSlideInViewChange} =
    useGetCenteredSlideIndex({slideCount});

  const activeSlideIndex =
    addFakeOuterSlideElements && currentSlide ? currentSlide + 1 : currentSlide;

  const [scrollBehavior, setScrollBehavior] = React.useState<'smooth' | 'auto'>(
    'auto',
  );

  const onStoryEnd = useCallback(
    (index: number) => () => {
      if (centeredHighlightIndex !== index || !isOpen) {
        return;
      }

      const nextHighlightIndex = index + 1;

      if (highlights.length === nextHighlightIndex) {
        hide();
        return;
      }

      if (nextHighlightIndex < highlights.length) {
        if (scrollBehavior !== 'smooth') {
          setScrollBehavior('smooth');
        }
        openHighlight(nextHighlightIndex);
      }
    },
    [
      centeredHighlightIndex,
      hide,
      highlights.length,
      isOpen,
      openHighlight,
      scrollBehavior,
    ],
  );

  // set the scroll behavior to smooth when the slider is closed so that it doesn't smooth scroll to an item when the slider is opened again with another highlight selected
  useEffect(() => {
    if (!isOpen) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setScrollBehavior('auto');
    }
  }, [isOpen]);

  // This is a workaround to leave empty space on the left and right side of the first and last element in desktop mode
  const placeholderSlideElement = (position: 'start' | 'end') => {
    const index = position === 'start' ? -1 : highlights.length;
    return addFakeOuterSlideElements ? (
      <HighlightSlide
        key={`fake-item-${position}`}
        highlightId={0}
        isHidden
        slides={highlights[0]!.slides}
        handleSlideInViewChange={handleSlideInViewChange(index)}
      />
    ) : null;
  };

  return (
    <Fade mounted={isOpen} duration={500}>
      <HighlightsZoomSliderWrapper
        data-testid="ZoomSliderOverlay"
        onClick={hide}
      >
        <StyledCloseButton
          size="M"
          asset={GenericIconName.Close}
          fill={COLORS.White}
          onClick={hide}
          data-testid="ZoomSliderCloseButton"
        />
        <StyledZoomSlider
          disableHeaderControl
          dataTestId="ZoomSlider"
          currentCenteredItem={{index: activeSlideIndex, scrollBehavior}}
          slideCount={slideCount}
          constrained={disableNavigationControls}
          hideSideButtons
          enableKeyboardNavigation
          scrollSingleElementOnNavigation
          SlideControlComponentBottom={
            disableNavigationControls ? undefined : SingleItemNavigation
          }
          disableOverscroll={disableOverscroll}
        >
          {placeholderSlideElement('start')}
          {highlights.map((highlight, index) => {
            return (
              <ErrorBoundary
                key={index}
                renderContext="discover_highlight_zoom_slide"
              >
                <HighlightSlide
                  highlightId={highlight.highlightId}
                  slides={highlight.slides}
                  isActive={index === centeredHighlightIndex}
                  handleSlideInViewChange={handleSlideInViewChange(index)}
                  onStoryEnd={onStoryEnd(index)}
                />
              </ErrorBoundary>
            );
          })}
          {placeholderSlideElement('end')}
        </StyledZoomSlider>
      </HighlightsZoomSliderWrapper>
    </Fade>
  );
};

const SingleItemNavigation: React.FC<{
  scrollToNextItem: () => void;
  scrollToPrevItem: () => void;
  prevIsActive: boolean;
  nextIsActive: boolean;
}> = ({scrollToNextItem, scrollToPrevItem, prevIsActive, nextIsActive}) => {
  const {t} = useTranslation();

  return (
    <NavButtonsWrapper
      onClick={event => {
        event.stopPropagation();
      }}
      data-testid="NavButtonsWrapper"
    >
      {prevIsActive && (
        <ButtonWrapper
          data-align="start"
          onClick={scrollToPrevItem}
          aria-label={t('Go to the previous slide')}
          variant="floating"
          size="sm"
        >
          <Icon
            size="S"
            asset={GenericIconName.ArrowLeft}
            fill={COLORS.Black}
            data-testid="NavPrevButton"
          />
        </ButtonWrapper>
      )}
      {nextIsActive && (
        <ButtonWrapper
          data-align="end"
          onClick={scrollToNextItem}
          aria-label={t('Go to the next slide')}
          variant="floating"
          size="sm"
        >
          <Icon
            size="S"
            asset={GenericIconName.ArrowRight}
            fill={COLORS.Black}
            data-testid="NavNextButton"
          />
        </ButtonWrapper>
      )}
    </NavButtonsWrapper>
  );
};

const NavButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  height: 0;
  margin: auto;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  align-items: center;
  width: 40%;
  ${desktopAndUp(css`
    width: 35%;
  `)}
`;

const ButtonWrapper = styled(Button)`
  background-color: white;
  border-radius: 50%;
  padding: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  && {
    position: absolute;
  }

  &[data-align='start'] {
    left: 0px;

    &::after {
      right: 0;
      transform: translate(-50%, -50%);
    }
  }

  &[data-align='end'] {
    right: 0px;

    &::after {
      right: 0;
      transform: translate(100%, -50%);
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    background-color: transparent;
    width: 25vw;
    height: 70vh;
  }
`;

const StyledZoomSlider = styled(HorizontalSlider)`
  width: 100%;
  height: 100%;
`;

const HighlightsZoomSliderWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${Z_INDEXES.MODAL_DIALOG_WRAPPER};

  padding: 0;
  ${desktopAndUp(css`
    padding: 30px;
  `)}

  backdrop-filter: blur(5px);
  background: linear-gradient(0deg, #000000eb 0%, rgb(0 0 0 / 65%) 100%);

  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledCloseButton = styled(Icon)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: ${Z_INDEXES.MODAL_DIALOG_CLOSE_BUTTON};
  cursor: pointer;

  transition: transform 0.2s ease-in-out;
  &:hover {
    transform: rotate(90deg);
  }
`;
